import type { ReactNode } from "react";

export function LoadingBlock({
  className = "h-5 w-full",
}: {
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`loading-block bg-ink/10 block ${className}`}
    />
  );
}

/**
 * Generic page skeleton. `header={false}` when the page already renders its
 * own title, so the placeholder doesn't repeat it.
 */
export function PanelSkeleton({
  cards = 3,
  header = true,
}: {
  cards?: number;
  header?: boolean;
}) {
  return (
    <div role="status" aria-label="Cargando contenido" className="space-y-5">
      {header && (
        <div className="space-y-3">
          <LoadingBlock className="h-7 w-28" />
          <LoadingBlock className="h-10 w-72 max-w-full" />
          <LoadingBlock className="h-4 w-96 max-w-full" />
        </div>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        {Array.from({ length: cards }, (_, index) => (
          <div
            key={index}
            className="border-ink bg-bone border-surface shadow-brutal"
          >
            <LoadingBlock className="aspect-video h-auto w-full" />
            <div className="p-5">
              <LoadingBlock className="h-3 w-32" />
              <LoadingBlock className="mt-3 h-7 w-3/4" />
              <LoadingBlock className="mt-3 h-4 w-full" />
              <LoadingBlock className="mt-2 h-4 w-2/3" />
            </div>
          </div>
        ))}
      </div>
      <span className="sr-only">Cargando…</span>
    </div>
  );
}

// ── Page-shaped skeletons ──────────────────────────────────────────────────
// Each mirrors the layout it stands in for, so content lands where the
// placeholder was instead of reflowing the page.

function SkeletonStatus({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div role="status" aria-label={label} className={className}>
      {children}
      <span className="sr-only">Cargando…</span>
    </div>
  );
}

/** Overview: "Sigue viendo tu clase" card (thumbnail + lesson info). */
export function ContinueSkeleton() {
  return (
    <SkeletonStatus label="Cargando tu clase" className="mt-16">
      <LoadingBlock className="h-3 w-40" />
      <LoadingBlock className="mt-4 h-9 w-80 max-w-full" />
      <div className="border-ink bg-bone border-surface shadow-brutal-lg mt-7 grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <LoadingBlock className="aspect-video h-auto w-full md:aspect-auto md:min-h-80" />
        <div className="flex flex-col justify-center p-6 sm:p-8">
          <LoadingBlock className="h-3 w-48" />
          <LoadingBlock className="mt-3 h-8 w-4/5" />
          <LoadingBlock className="mt-5 h-4 w-full" />
          <LoadingBlock className="mt-2 h-4 w-11/12" />
          <LoadingBlock className="mt-2 h-4 w-2/3" />
          <LoadingBlock className="mt-5 h-3 w-40" />
          <LoadingBlock className="rounded-control mt-6 h-12 w-full" />
        </div>
      </div>
    </SkeletonStatus>
  );
}

/** Overview: the Próximamente filed cards. */
export function ComingSoonSkeleton() {
  return (
    <SkeletonStatus label="Cargando próximos cursos" className="mt-20">
      <LoadingBlock className="h-24 w-full" />
      <LoadingBlock className="mt-10 h-3 w-28" />
      <LoadingBlock className="mt-4 h-12 w-72 max-w-full" />
      <div className="mt-12 grid items-start gap-6 md:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className={index === 1 ? "md:mt-8" : index === 2 ? "md:mt-16" : ""}
          >
            <LoadingBlock className="h-8 w-40" />
            <div className="border-ink bg-bone border-surface">
              <LoadingBlock className="aspect-[3/2] h-auto w-full" />
              <div className="p-5">
                <LoadingBlock className="h-3 w-28" />
                <LoadingBlock className="mt-4 h-8 w-4/5" />
                <LoadingBlock className="mt-4 h-4 w-full" />
                <LoadingBlock className="mt-2 h-4 w-2/3" />
                <LoadingBlock className="mt-6 h-5 w-44" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </SkeletonStatus>
  );
}

/** Course player: video and lesson info left, progress + modules right. */
export function PlayerSkeleton() {
  return (
    <SkeletonStatus
      label="Cargando el curso"
      className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,30rem)]"
    >
      <div className="min-w-0">
        <div className="border-ink bg-ink border-surface aspect-video" />
        <LoadingBlock className="mt-6 h-3 w-40" />
        <LoadingBlock className="mt-3 h-9 w-3/4" />
        <div className="mt-5 flex gap-3">
          <LoadingBlock className="rounded-control h-11 w-44" />
          <LoadingBlock className="rounded-control h-11 w-36" />
        </div>
        <LoadingBlock className="mt-7 h-4 w-full" />
        <LoadingBlock className="mt-2 h-4 w-11/12" />
        <LoadingBlock className="mt-2 h-4 w-3/5" />
      </div>
      <div className="flex flex-col gap-5">
        <div className="border-ink bg-bone border-surface shadow-brutal">
          <LoadingBlock className="h-11 w-full" />
          <div className="p-5">
            <LoadingBlock className="h-3 w-36" />
            <LoadingBlock className="mt-4 h-2 w-full" />
            <LoadingBlock className="mt-3 h-3 w-44" />
          </div>
        </div>
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className="border-ink bg-bone border-surface shadow-brutal flex items-center gap-3 p-4"
          >
            <LoadingBlock className="h-9 w-9 shrink-0" />
            <div className="min-w-0 flex-1">
              <LoadingBlock className="h-3 w-32" />
              <LoadingBlock className="mt-2 h-5 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    </SkeletonStatus>
  );
}

/** Lesson companion: action row, tabs and a few lines of content. */
export function CompanionSkeleton() {
  return (
    <SkeletonStatus
      label="Cargando herramientas de la clase"
      className="border-ink bg-bone border-surface p-5"
    >
      <div className="flex flex-wrap gap-2">
        <LoadingBlock className="rounded-control h-11 w-36" />
      </div>
      <div className="mt-5 flex gap-2">
        {[0, 1, 2].map((index) => (
          <LoadingBlock key={index} className="h-8 w-24" />
        ))}
      </div>
      <LoadingBlock className="mt-5 h-4 w-full" />
      <LoadingBlock className="mt-2 h-4 w-4/5" />
    </SkeletonStatus>
  );
}

/** Whole app while the session loads: header, ink sidebar, overview. */
/**
 * The whole app while the session loads: header, the student sidebar at its
 * remembered width (wide or collapsed to icons) and, in the content area,
 * the skeleton of the page being opened (`children`); Overview by default.
 */
export function AppShellSkeleton({ children }: { children?: ReactNode }) {
  const collapsed =
    typeof localStorage !== "undefined" &&
    localStorage.getItem("hey404:sidebar-collapsed") === "1";
  return (
    <SkeletonStatus label="Cargando hey404" className="bg-paper min-h-dvh">
      <div className="border-ink border-b-(length:--border-surface) flex h-16 items-center px-5">
        <img src="/logo-final.svg" alt="" className="h-10 w-auto" />
      </div>
      <div
        className={
          collapsed
            ? "grid md:grid-cols-[76px_minmax(0,1fr)]"
            : "grid md:grid-cols-[256px_minmax(0,1fr)]"
        }
      >
        <div
          className={`bg-ink border-ink border-r-(length:--border-surface) hidden h-[calc(100dvh-64px)] flex-col gap-2 py-5 md:flex ${collapsed ? "items-center px-2" : "px-3"}`}
        >
          {!collapsed && (
            <span className="loading-block bg-paper/15 mb-3 block h-3 w-24" />
          )}
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <span
              key={index}
              className={`loading-block bg-paper/10 rounded-control block h-11 ${collapsed ? "w-11" : "w-full"}`}
            />
          ))}
          {!collapsed && (
            <span className="loading-block bg-paper/10 rounded-control mt-4 block h-36 w-full" />
          )}
        </div>
        <div className="platform-grid min-w-0 p-5 sm:p-8 lg:p-12">
          {children ?? (
            <>
              <LoadingBlock className="h-7 w-28" />
              <LoadingBlock className="mt-5 h-12 w-80 max-w-full" />
              <LoadingBlock className="mt-4 h-4 w-72 max-w-full" />
              <div className="mt-10 grid gap-6 sm:grid-cols-2">
                <LoadingBlock className="h-48 w-full" />
                <LoadingBlock className="h-48 w-full" />
              </div>
              <ContinueSkeleton />
            </>
          )}
        </div>
      </div>
    </SkeletonStatus>
  );
}

/** Interests: narrow header, a 2-col option grid and a 3-col level grid. */
export function InterestsSkeleton() {
  return (
    <SkeletonStatus label="Cargando" className="mx-auto max-w-xl">
      <LoadingBlock className="h-7 w-36" />
      <LoadingBlock className="mt-5 h-12 w-80 max-w-full" />
      <LoadingBlock className="mt-4 h-4 w-full" />
      <LoadingBlock className="mt-8 h-3 w-40" />
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {Array.from({ length: 6 }, (_, index) => (
          <LoadingBlock key={index} className="h-12 w-full" />
        ))}
      </div>
      <LoadingBlock className="mt-6 h-3 w-32" />
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <LoadingBlock key={index} className="h-12 w-full" />
        ))}
      </div>
      <LoadingBlock className="rounded-control mt-8 h-12 w-full" />
    </SkeletonStatus>
  );
}

/** Inside a lesson tab (quiz, project): a title and a few rows, no header. */
export function TabPanelSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <SkeletonStatus label="Cargando" className="space-y-3">
      <LoadingBlock className="h-6 w-2/3" />
      {Array.from({ length: rows }, (_, index) => (
        <LoadingBlock key={index} className="h-11 w-full" />
      ))}
      <LoadingBlock className="rounded-control h-11 w-40" />
    </SkeletonStatus>
  );
}

// Admin skeleton pieces. The admin PageHeader (sticker, title, description)
// is already on screen while a section loads, so these only draw the section
// body.
const AdminCard = ({ children }: { children: ReactNode }) => (
  <div className="border-ink bg-bone border-surface p-5">{children}</div>
);

/** A section title with its count chip, as above admin lists and tables. */
const AdminCountTitle = () => (
  <div className="flex items-center gap-3">
    <LoadingBlock className="h-7 w-40" />
    <LoadingBlock className="h-5 w-8" />
  </div>
);

const AdminTable = ({ rows = 6 }: { rows?: number }) => (
  <div className="border-ink bg-bone border-surface mt-3 p-5">
    <LoadingBlock className="h-4 w-full" />
    {Array.from({ length: rows }, (_, index) => (
      <LoadingBlock key={index} className="mt-4 h-9 w-full" />
    ))}
  </div>
);

/** A titled panel with compact rows, as in Academia. */
const AdminPanelRows = ({ rows }: { rows: number }) => (
  <div className="border-ink bg-bone border-surface">
    <div className="border-ink/20 flex items-center justify-between border-b border-dashed px-5 py-4">
      <LoadingBlock className="h-6 w-40" />
      <LoadingBlock className="rounded-control h-9 w-28" />
    </div>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="flex items-center gap-4 px-5 py-3.5">
        <LoadingBlock className="h-10 w-10 shrink-0" />
        <div className="min-w-0 flex-1">
          <LoadingBlock className="h-4 w-1/2" />
          <LoadingBlock className="mt-2 h-3 w-1/3" />
        </div>
        <LoadingBlock className="rounded-control hidden h-9 w-28 sm:block" />
      </div>
    ))}
  </div>
);

/** Stacked record cards (people, administrators, pending posts). */
const AdminRecords = ({
  count = 5,
  avatar = false,
  actions = 1,
}: {
  count?: number;
  avatar?: boolean;
  actions?: number;
}) => (
  <div className="mt-5 space-y-3">
    {Array.from({ length: count }, (_, index) => (
      <div
        key={index}
        className="border-ink bg-bone border-chip flex items-center gap-4 p-4"
      >
        {avatar && <LoadingBlock className="h-10 w-10 shrink-0" />}
        <div className="min-w-0 flex-1">
          <LoadingBlock className="h-4 w-1/2" />
          <LoadingBlock className="mt-2 h-3 w-1/3" />
        </div>
        <div className="hidden gap-2 sm:flex">
          {Array.from({ length: actions }, (_, action) => (
            <LoadingBlock
              key={action}
              className="rounded-control h-9 w-24 shrink-0"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/** The body of each admin section while its data loads, one per layout. */
export function AdminSkeleton({
  variant,
}: {
  variant:
    | "overview"
    | "commerce"
    | "administrators"
    | "people"
    | "learning"
    | "moderation"
    | "audit";
}) {
  return (
    <SkeletonStatus label="Cargando administración">
      {variant === "overview" && (
        <>
          <LoadingBlock className="h-8 w-56" />
          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: 5 }, (_, index) => (
              <AdminCard key={index}>
                <LoadingBlock className="h-3 w-20" />
                <LoadingBlock className="mt-3 h-12 w-16" />
              </AdminCard>
            ))}
          </div>
          <div className="mt-7">
            <AdminCard>
              <LoadingBlock className="h-6 w-48" />
              <LoadingBlock className="mt-3 h-4 w-full max-w-xl" />
              <LoadingBlock className="mt-2 h-4 w-2/3 max-w-md" />
            </AdminCard>
          </div>
        </>
      )}
      {variant === "commerce" && (
        <>
          <AdminCard>
            <LoadingBlock className="h-6 w-64 max-w-full" />
            <LoadingBlock className="mt-2 h-4 w-full max-w-lg" />
            <div className="mt-4 flex flex-wrap gap-3">
              <LoadingBlock className="h-12 w-72 max-w-full" />
              <LoadingBlock className="h-12 w-40" />
              <LoadingBlock className="rounded-control h-12 w-28" />
            </div>
          </AdminCard>
          {[0, 1, 2].map((table) => (
            <div key={table} className="mt-8">
              <AdminCountTitle />
              <AdminTable rows={4} />
            </div>
          ))}
        </>
      )}
      {variant === "administrators" && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <AdminCountTitle />
            <LoadingBlock className="rounded-control h-12 w-36" />
          </div>
          <AdminRecords count={4} avatar />
        </>
      )}
      {variant === "people" && (
        <>
          <div className="flex flex-wrap gap-3">
            <div className="flex min-w-[min(100%,18rem)] flex-1 gap-2">
              <LoadingBlock className="h-12 flex-1" />
              <LoadingBlock className="rounded-control h-12 w-24" />
            </div>
            <LoadingBlock className="rounded-control h-12 w-44" />
          </div>
          <div className="border-ink bg-bone border-chip mt-5">
            {Array.from({ length: 8 }, (_, index) => (
              <div
                key={index}
                className="border-ink/15 flex items-center gap-6 border-b px-5 py-3.5 last:border-b-0"
              >
                <LoadingBlock className="h-9 w-9 shrink-0" />
                <div className="min-w-0 flex-1">
                  <LoadingBlock className="h-4 w-1/3" />
                  <LoadingBlock className="mt-1.5 h-3 w-20" />
                </div>
                <LoadingBlock className="hidden h-5 w-40 md:block" />
                <LoadingBlock className="hidden h-3 w-20 md:block" />
                <LoadingBlock className="hidden h-4 w-16 md:block" />
                <LoadingBlock className="rounded-control h-9 w-9" />
              </div>
            ))}
          </div>
        </>
      )}
      {variant === "learning" && (
        <>
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <AdminCard key={index}>
                <LoadingBlock className="h-3 w-28" />
                <LoadingBlock className="mt-3 h-12 w-12" />
              </AdminCard>
            ))}
          </div>
          <div className="mt-8">
            <AdminPanelRows rows={3} />
          </div>
          <div className="mt-8 grid items-start gap-6 xl:grid-cols-2">
            {Array.from({ length: 4 }, (_, index) => (
              <AdminPanelRows key={index} rows={3} />
            ))}
          </div>
        </>
      )}
      {variant === "moderation" && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <LoadingBlock className="rounded-control h-12 w-96 max-w-full" />
            <LoadingBlock className="rounded-control h-11 w-56" />
          </div>
          <div className="mt-8">
            <AdminCountTitle />
            <div className="border-ink bg-bone border-chip mt-4">
              {Array.from({ length: 3 }, (_, index) => (
                <div
                  key={index}
                  className="border-ink/15 flex items-start gap-4 border-b border-dashed p-5 last:border-b-0"
                >
                  <LoadingBlock className="h-8 w-8 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <LoadingBlock className="h-4 w-1/3" />
                    <LoadingBlock className="mt-3 h-4 w-2/3" />
                    <LoadingBlock className="mt-2 h-3 w-1/2" />
                  </div>
                  <LoadingBlock className="rounded-control hidden h-9 w-44 sm:block" />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {variant === "audit" && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <AdminCountTitle />
            <div className="flex gap-3">
              <LoadingBlock className="rounded-control h-12 w-72 max-w-full" />
              <LoadingBlock className="rounded-control hidden h-12 w-48 sm:block" />
            </div>
          </div>
          <LoadingBlock className="mt-6 h-3 w-16" />
          <div className="border-ink bg-bone border-chip mt-3">
            {Array.from({ length: 6 }, (_, index) => (
              <div
                key={index}
                className="border-ink/15 flex items-start gap-4 border-b px-5 py-4 last:border-b-0"
              >
                <LoadingBlock className="h-9 w-9 shrink-0" />
                <div className="min-w-0 flex-1">
                  <LoadingBlock className="h-4 w-2/3" />
                  <LoadingBlock className="mt-2 h-3 w-24" />
                </div>
                <LoadingBlock className="h-3 w-10" />
              </div>
            ))}
          </div>
        </>
      )}
    </SkeletonStatus>
  );
}

/**
 * Admin shell while it boots: the ink sidebar at its remembered width, the
 * page header (sticker, title, description) and the overview body.
 */
export function AdminShellSkeleton() {
  const collapsed = localStorage.getItem("hey404:sidebar-collapsed") === "1";
  return (
    <div className="flex min-h-[calc(100dvh-64px)] flex-col md:flex-row">
      <div
        className={`bg-ink border-ink md:border-r-(length:--border-surface) hidden shrink-0 flex-col gap-1 py-4 md:sticky md:top-16 md:flex md:h-[calc(100dvh-64px)] ${collapsed ? "items-center px-2 md:w-[76px]" : "px-4 md:w-[236px]"}`}
      >
        <span
          className={`loading-block bg-paper/15 mb-5 mt-1 block h-8 ${collapsed ? "w-9" : "w-36"}`}
        />
        {Array.from({ length: 9 }, (_, index) => (
          <span
            key={index}
            className={`loading-block bg-paper/10 block h-11 ${collapsed ? "w-11" : "w-full"}`}
          />
        ))}
        <span
          className={`loading-block bg-paper/10 mt-auto block h-11 ${collapsed ? "w-11" : "w-full"}`}
        />
      </div>
      <div className="min-w-0 flex-1 p-5 sm:p-8">
        <div className="space-y-4">
          <LoadingBlock className="h-7 w-36" />
          <LoadingBlock className="h-12 w-72 max-w-full" />
          <LoadingBlock className="h-4 w-96 max-w-full" />
        </div>
        <div className="mt-9">
          <AdminSkeleton variant="overview" />
        </div>
      </div>
    </div>
  );
}
